/** Pure helpers of the notes feed (unit tested; no React, no I/O). */
import type { Note } from './types';

export type NoteAnchor =
  { after_note_id: string } | { before_note_id: string } | Record<string, never>;

/**
 * Where a note dropped on `targetId` (upper / lower half) ends up, as the API anchor, plus the
 * resulting order of ids. `null` when nothing would change.
 */
export function noteDrop(
  ids: readonly string[],
  noteId: string,
  targetId: string,
  edge: 'before' | 'after',
): { anchor: NoteAnchor; order: string[] } | null {
  if (noteId === targetId) return null;
  const rest = ids.filter((id) => id !== noteId);
  const targetIndex = rest.indexOf(targetId);
  if (targetIndex === -1 || !ids.includes(noteId)) return null;
  const index = edge === 'before' ? targetIndex : targetIndex + 1;
  const order = [...rest.slice(0, index), noteId, ...rest.slice(index)];
  if (order.every((id, position) => id === ids[position])) return null;
  const before = order[index + 1];
  const after = order[index - 1];
  // The API places a note relative to ONE neighbour; the one below is preferred.
  if (before !== undefined) return { anchor: { before_note_id: before }, order };
  if (after !== undefined) return { anchor: { after_note_id: after }, order };
  return null;
}

/** One step up / down in the feed (the button alternative to dragging). */
export function noteStep(
  ids: readonly string[],
  noteId: string,
  direction: 'up' | 'down',
): { anchor: NoteAnchor; order: string[] } | null {
  const index = ids.indexOf(noteId);
  const neighbour = ids[direction === 'up' ? index - 1 : index + 1];
  if (index === -1 || neighbour === undefined) return null;
  return noteDrop(ids, noteId, neighbour, direction === 'up' ? 'before' : 'after');
}

export function reorderNotes(notes: readonly Note[], order: readonly string[]): Note[] {
  const byId = new Map(notes.map((note) => [note.id, note]));
  const known = order.flatMap((id) => byId.get(id) ?? []);
  const rest = notes.filter((note) => !order.includes(note.id));
  return [...known, ...rest];
}
